
export interface StoryPrompt {
  id: string;
  category: string;
  title: string;
  description: string;
  suggestedTitle?: string;
  starterText?: string;
}

export const storyPromptCategories = [
  {
    id: 'childhood',
    name: 'Childhood & Early Life',
    icon: '🌱',
    description: 'Early memories and formative experiences'
  },
  {
    id: 'making-life',
    name: 'Making of a Life',
    icon: '🏗️',
    description: 'Building independence and finding your path'
  },
  {
    id: 'family',
    name: 'Family & Relationships',
    icon: '❤️',
    description: 'Stories about loved ones and relationships'
  },
  {
    id: 'achievements',
    name: 'Achievements & Milestones',
    icon: '🏆',
    description: 'Proud moments and accomplishments'
  },
  {
    id: 'adventures',
    name: 'Adventures & Travel',
    icon: '✈️',
    description: 'Journeys and memorable experiences'
  },
  {
    id: 'wisdom',
    name: 'Wisdom & Life Lessons',
    icon: '💡',
    description: 'Values, beliefs, and lessons learned'
  },
  {
    id: 'legacy',
    name: 'Legacy & Impact',
    icon: '🌟',
    description: 'How they touched others\' lives'
  },
  {
    id: 'other',
    name: 'Other Stories',
    icon: '📝',
    description: 'All other memories and experiences'
  }
];

export const storyPrompts: StoryPrompt[] = [
  // Childhood & Early Life
  {
    id: 'first-memory',
    category: 'childhood',
    title: 'A cherished childhood memory',
    description: 'Share one of their earliest or most treasured memories from childhood',
    suggestedTitle: 'A Special Childhood Memory',
    starterText: 'One of the most vivid memories from their childhood was...'
  },
  {
    id: 'childhood-home',
    category: 'childhood',
    title: 'The home they grew up in',
    description: 'Describe the house, neighborhood, or place that shaped their early years',
    suggestedTitle: 'Where It All Began',
    starterText: 'The place they called home during their childhood was...'
  },
  {
    id: 'school-days',
    category: 'childhood',
    title: 'School days and learning',
    description: 'A memorable experience from their school years',
    suggestedTitle: 'School Days Remembered',
    starterText: 'During their school years, there was a time when...'
  },

  // Making of a Life
  {
    id: 'first-independence',
    category: 'making-life',
    title: 'First steps of independence',
    description: 'The moment they first lived on their own or made their own decisions',
    suggestedTitle: 'On My Own',
    starterText: 'The first time they truly felt independent was when...'
  },
  {
    id: 'building-home',
    category: 'making-life',
    title: 'Creating their own space',
    description: 'Making a place truly their own - first apartment, home, or special room',
    suggestedTitle: 'A Place to Call Home',
    starterText: 'When they finally had their own space, they...'
  },
  {
    id: 'finding-passion',
    category: 'making-life',
    title: 'Discovering their calling',
    description: 'The moment they found something they truly loved doing',
    suggestedTitle: 'Finding My Path',
    starterText: 'They discovered their true passion when...'
  },
  {
    id: 'adult-responsibilities',
    category: 'making-life',
    title: 'Learning to be an adult',
    description: 'A story about navigating grown-up responsibilities for the first time',
    suggestedTitle: 'Growing Up',
    starterText: 'One of the first times they felt like a real adult was...'
  },

  // Family & Relationships
  {
    id: 'love-story',
    category: 'family',
    title: 'How they met their life partner',
    description: 'Tell the story of how they found love',
    suggestedTitle: 'A Love Story',
    starterText: 'The story of how they met the love of their life begins...'
  },
  {
    id: 'becoming-parent',
    category: 'family',
    title: 'Becoming a parent/grandparent',
    description: 'The joy and transformation of welcoming children into their life',
    suggestedTitle: 'The Day Everything Changed',
    starterText: 'When they first became a parent, everything changed...'
  },
  {
    id: 'family-tradition',
    category: 'family',
    title: 'A beloved family tradition',
    description: 'A special custom or tradition they cherished with family',
    suggestedTitle: 'Our Family Tradition',
    starterText: 'Every year, our family would gather to...'
  },

  // Achievements & Milestones
  {
    id: 'proudest-moment',
    category: 'achievements',
    title: 'Their proudest achievement',
    description: 'Something they accomplished that brought them immense pride',
    suggestedTitle: 'A Moment of Pride',
    starterText: 'One of their proudest achievements was...'
  },
  {
    id: 'career-highlight',
    category: 'achievements',
    title: 'A career milestone',
    description: 'A significant moment or achievement in their professional life',
    suggestedTitle: 'Professional Success',
    starterText: 'In their career, there was a defining moment when...'
  },
  {
    id: 'overcoming-challenge',
    category: 'achievements',
    title: 'Overcoming a challenge',
    description: 'How they faced and conquered a difficult situation',
    suggestedTitle: 'Rising Above',
    starterText: 'When faced with a significant challenge, they...'
  },

  // Adventures & Travel
  {
    id: 'favorite-adventure',
    category: 'adventures',
    title: 'Their greatest adventure',
    description: 'An exciting journey or experience they loved to share',
    suggestedTitle: 'An Unforgettable Adventure',
    starterText: 'One adventure they would never forget was...'
  },
  {
    id: 'special-place',
    category: 'adventures',
    title: 'A place close to their heart',
    description: 'Somewhere special they loved to visit or felt connected to',
    suggestedTitle: 'A Special Place',
    starterText: 'There was a place that held special meaning for them...'
  },
  {
    id: 'unexpected-journey',
    category: 'adventures',
    title: 'An unexpected journey',
    description: 'A trip or experience that took an surprising turn',
    suggestedTitle: 'When Plans Changed',
    starterText: 'What started as an ordinary day turned into...'
  },

  // Wisdom & Life Lessons
  {
    id: 'life-philosophy',
    category: 'wisdom',
    title: 'Words they lived by',
    description: 'Their personal motto, saying, or philosophy that guided them',
    suggestedTitle: 'Words to Live By',
    starterText: 'They always believed that...'
  },
  {
    id: 'lesson-learned',
    category: 'wisdom',
    title: 'An important lesson they learned',
    description: 'A valuable insight they gained through experience',
    suggestedTitle: 'A Lesson Worth Learning',
    starterText: 'Through experience, they learned that...'
  },
  {
    id: 'advice-given',
    category: 'wisdom',
    title: 'Advice they often shared',
    description: 'Wisdom they passed on to others',
    suggestedTitle: 'Wisdom Shared',
    starterText: 'They often told people...'
  },

  // Legacy & Impact
  {
    id: 'helping-others',
    category: 'legacy',
    title: 'How they helped others',
    description: 'A time they made a difference in someone else\'s life',
    suggestedTitle: 'Making a Difference',
    starterText: 'They had a special way of helping others, like when...'
  },
  {
    id: 'community-impact',
    category: 'legacy',
    title: 'Their contribution to the community',
    description: 'How they gave back or made their community better',
    suggestedTitle: 'Giving Back',
    starterText: 'In their community, they were known for...'
  },
  {
    id: 'lasting-influence',
    category: 'legacy',
    title: 'How they influenced others',
    description: 'The positive impact they had on people around them',
    suggestedTitle: 'A Lasting Influence',
    starterText: 'They had a way of inspiring others by...'
  }
];

export const getPromptsByCategory = (categoryId: string): StoryPrompt[] => {
  return storyPrompts.filter(prompt => prompt.category === categoryId);
};

export const getPromptById = (id: string): StoryPrompt | undefined => {
  return storyPrompts.find(prompt => prompt.id === id);
};
